import SearchIcon from '@mui/icons-material/Search';
import { Box, List, Paper, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { Fight } from '../../../../../../../corelogic/domain/models/fight.model';
import { Fighter } from '../../../../../../../corelogic/domain/models/fighter.model';
import { FighterWithDescription } from '../../../../../../../corelogic/domain/models/hub.model';
import {
  buildFighterKey,
  getDefaultFighterImage,
} from '../../../../../../../corelogic/domain/utils/fighterImage.utils';
import { useAllFighterImagesState } from '../../../../hooks/useFighterImage';
import { FighterImageService } from '../../../../services/FighterImage.service';
import FighterToWatchItem from './FighterToWatchItem';

interface FighterToWatchListProps {
  fighters: FighterWithDescription[];
  allFights: Fight[];
  onFighterClick: (fighter: Fighter) => void;
}

const paperStyle = {
  bgcolor: 'rgba(0, 0, 0, 0.85)',
  overflow: 'hidden',
  height: '500px',
};

const sectionTitleStyles = {
  fontFamily: 'Bebas Neue',
  fontSize: 'clamp(0.8rem, 3vw, 1.5rem)',
  color: 'white',
};

const contentBoxStyle = {
  p: 2,
  height: 'calc(100% - 64px)',
  overflow: 'auto',
};

const sectionHeaderStyle = {
  p: 2,
  borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  bgcolor: 'rgba(255, 255, 255, 0.1)',
  color: 'white',
};

const SectionHeader: React.FC<{
  icon?: React.ReactNode;
  title: React.ReactNode;
}> = ({ icon, title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {icon && icon}
    <Typography
      variant="h5"
      sx={{ ...sectionTitleStyles, fontSize: 'clamp(0.8rem, 3vw, 1.5rem)' }}
    >
      {title}
    </Typography>
  </Box>
);

const FighterToWatchList: React.FC<FighterToWatchListProps> = ({
  fighters,
  allFights,
  onFighterClick,
}) => {
  // Memoize the fighters array to prevent infinite loop in useAllFighterImagesState
  const memoizedFighters = useMemo(
    () => fighters.map((f) => f.fighter),
    [fighters],
  );

  // Load fighter images from S3
  const { fighterImages, loadingStates } =
    useAllFighterImagesState(memoizedFighters);

  // Request fighter images for all fighters
  useEffect(() => {
    fighters.forEach(({ fighter }) => {
      FighterImageService.retrieveFighterImage(
        fighter.firstname,
        fighter.lastname,
        fighter.dob,
        fighter.gender,
      );
    });
  }, [fighters]);

  return (
    <Paper sx={paperStyle}>
      <Box sx={sectionHeaderStyle}>
        <SectionHeader
          icon={<SearchIcon sx={{ color: '#cc0000' }} />}
          title="Fighters to Watch"
        />
      </Box>
      <Box sx={contentBoxStyle}>
        <List>
          {fighters.map(({ fighter, description, fightId }) => {
            const fight = allFights.find((f) => f.id === fightId);
            const opponentName = fight
              ? fight.fighter_1_id === fighter.id
                ? fight.fighter_2
                : fight.fighter_1
              : 'Unknown';
            const eventName = fight ? fight.name : 'Unknown Event';
            const isTitleFight = fight?.title_fight === 'T';

            // Get fighter image from S3 or fall back to default
            const fighterKey = buildFighterKey(
              fighter.firstname,
              fighter.lastname,
              fighter.dob,
            );
            const fighterImage =
              (fighterKey && fighterImages[fighterKey]) ||
              getDefaultFighterImage(fighter.gender);

            // Check if this specific fighter's image is loading
            const isImageLoading = fighterKey
              ? loadingStates[fighterKey] || false
              : false;

            return (
              <FighterToWatchItem
                key={fighter.id}
                name={`${fighter.firstname} ${fighter.lastname}`}
                image={fighterImage}
                opponent={opponentName}
                event={eventName}
                description={description}
                onClick={() => onFighterClick(fighter)}
                isTitleFight={isTitleFight}
                isLoading={isImageLoading}
              />
            );
          })}
        </List>
      </Box>
    </Paper>
  );
};

export default FighterToWatchList;
